import { z } from 'zod';
import { idSchema, isoDateTime, platformSchema } from './common.js';
export const toneSchema = z.enum([
    'professional',
    'casual',
    'bold',
    'witty',
    'inspirational',
    'educational',
]);
export const lengthSchema = z.enum(['short', 'medium', 'long']);
export const sourceModeSchema = z.enum(['idea', 'url', 'library', 'repurpose']);
export const generateInput = z.object({
    project_id: idSchema,
    source_mode: sourceModeSchema,
    source_url: z.string().url().optional(),
    source_idea: z.string().min(10).max(2000).optional(),
    source_content_id: idSchema.optional(),
    source_post_id: idSchema.optional(),
    platforms: z.array(platformSchema).min(1).max(8),
    tone: toneSchema.default('professional'),
    length: lengthSchema.default('medium'),
    options: z
        .object({
        hashtags: z.boolean().default(true),
        emojis: z.boolean().default(true),
        language: z.string().default('en'),
    })
        .default({}),
});
export const generatedDraft = z.object({
    platform: platformSchema,
    post_type: z.string(),
    caption: z.string(),
    char_count: z.number().int(),
    char_limit: z.number().int(),
    hashtags: z.array(z.string()),
    warnings: z.array(z.string()).default([]),
});
export const generateOutput = z.object({
    ai_job_id: idSchema,
    credits_used: z.number().int(),
    credits_remaining: z.number().int(),
    drafts: z.array(generatedDraft),
});
export const validatorSeverity = z.enum(['success', 'suggestion', 'warning', 'blocking']);
export const validatorCheck = z.object({
    id: z.number().int(),
    severity: validatorSeverity,
    category: z.string(),
    message: z.string(),
    auto_fix: z.string().optional(),
});
export const validateInput = z.object({
    post_ids: z.array(idSchema).min(1).max(20),
});
export const validateOutput = z.object({
    ai_job_id: idSchema,
    credits_used: z.number().int(),
    credits_remaining: z.number().int(),
    results: z.array(z.object({
        post_id: idSchema,
        score: z.number().int().min(0).max(100),
        checks: z.array(validatorCheck),
    })),
});
// ─── Regenerate one platform draft ────────────────────────────────────────
export const regenerateInput = z.object({
    post_id: idSchema,
    tone: toneSchema.optional(),
    length: lengthSchema.optional(),
    options: z
        .object({
        hashtags: z.boolean().optional(),
        emojis: z.boolean().optional(),
    })
        .default({}),
});
export const regenerateOutput = z.object({
    ai_job_id: idSchema,
    credits_used: z.number().int(),
    credits_remaining: z.number().int(),
    draft: generatedDraft,
});
// ─── Repurpose existing post into other platforms ─────────────────────────
export const repurposeInput = z.object({
    post_id: idSchema,
    target_platforms: z.array(platformSchema).min(1).max(8),
    tone: toneSchema.default('professional'),
    length: lengthSchema.default('medium'),
    options: z
        .object({
        hashtags: z.boolean().default(true),
        emojis: z.boolean().default(true),
    })
        .default({}),
});
export const repurposeOutput = generateOutput; // same shape
// ─── Alt-text generation for media ────────────────────────────────────────
export const altTextInput = z.object({
    media_id: idSchema,
});
export const altTextOutput = z.object({
    ai_job_id: idSchema,
    credits_used: z.number().int(),
    credits_remaining: z.number().int(),
    alt_text: z.string(),
});
export const aiJob = z.object({
    id: idSchema,
    workspace_id: idSchema,
    user_id: idSchema,
    kind: z.enum(['generate', 'validate', 'repurpose', 'rewrite', 'summarize', 'alt_text']),
    provider: z.string(),
    model: z.string(),
    status: z.enum(['queued', 'running', 'done', 'failed']),
    credits_used: z.number().int(),
    prompt_tokens: z.number().int().nullable(),
    completion_tokens: z.number().int().nullable(),
    latency_ms: z.number().int().nullable(),
    created_at: isoDateTime,
    finished_at: isoDateTime.nullable(),
});
//# sourceMappingURL=ai.js.map